import React from "react";

import L from 'leaflet';
import {LayersControl} from "react-leaflet";
import GeoJSONPointsCollection from "../../../components/maps/GeoJSONMap/GeoJSONPointsCollection";
import GeoJSONMap from "../../../components/maps/GeoJSONMap";
import {useTranslation} from "react-i18next";
import LegendControl from "./LegendControl";


const markerIconFromSeverity = (severity) => {
    return new L.divIcon({
        className: 'marker-blue-1',
        html: `<div class="marker marker-pin-large" style="--marker-color: ${severity.color ?? '#000000'}"><div class='marker-shape marker-circle'><span class="material-symbols-rounded" aria-hidden="true" style="font-size: 20px">report</span></div></div>`,
        iconSize: [30, 30],
        iconAnchor: [0, 21] // not [0, 0] because I put the marker class inside the divIcon html to override the color.
    })
}

const createClusterIconFn = (severity) => {
    return function (cluster) {
        return new L.DivIcon({
            className: 'yetiweb-cluster-div',
            html: `<div style='--bg-color-out: ${severity.color}70; --bg-color-in: ${severity.color}BB' class='marker-cluster'></div><span>` + cluster.getChildCount() + '</span>',
            iconSize: new L.Point(40, 40),
        });
    }
}


const IncidentMap = ({mapRef, incidentsGeoJSON, incidentRegistry, incidentSettings, onFeatureClick, getSelectedFeatures}) => {
    const {t} = useTranslation(['incidents']);
    const incidentSeverityRegistry = incidentRegistry.data?.incidentSeverity ?? [];

    const incidentSeverityLegend = incidentSeverityRegistry.map(severity => ({
        ...severity,
        color: incidentSettings.data?.incidentSeverityColor?.[severity.code]
    }));

    const incidentsBySeverity = incidentSeverityLegend.map(severity => {
        const incidents = incidentsGeoJSON.features?.filter(feature => feature.properties.incidentSeverity === severity.code);
        return {
            incidentSeverity: severity,
            geoJSON: {...incidentsGeoJSON, features: incidents},
            markerIconFn: () => markerIconBySeverityCode[severity.code]
        }
    });

    const markerIconBySeverityCode = incidentSeverityLegend.reduce((acc, severity) => {
        acc[severity.code] = markerIconFromSeverity(severity);
        return acc;
    }, {});

    const renderFeatureTooltip = (feature) => {
        const {properties} = feature;

        const category = incidentRegistry.data?.incidentCategoryV2?.find((item => item.code === properties?.incidentCategory))?.description;
        const subcategory = incidentRegistry.data?.incidentSubcategory?.find((item => item.code === properties?.incidentSubcategory))?.description;

        return <div>
            <div>{properties?.date}</div>
            <div>{properties?.locationName}</div>
            <div>{category} - {subcategory}</div>
            <div>
                {t('incidentMap.tooltip.uninjured')} {properties?.numberOfUninjured}; {t('incidentMap.tooltip.injured')} {properties?.numberOfInjured}; {t('incidentMap.tooltip.deaths')} {properties?.numberOfDeaths}
            </div>
        </div>
    }

    return <GeoJSONMap mapRef={mapRef}
                       data={[]}>
        <LayersControl position="topright">
            {
                incidentsBySeverity.map(({incidentSeverity, geoJSON, markerIconFn}) => (
                    <LayersControl.Overlay name={incidentSeverity.description} checked>
                        <GeoJSONPointsCollection key={incidentSeverity.code}
                                                 featureCollection={geoJSON}
                                                 cluster
                                                 clusterGroupProps={{
                                                     maxClusterRadius: 30,
                                                     showCoverageOnHover: false,
                                                 }}
                                                 onFeatureClick={onFeatureClick}
                                                 clusterIconFn={createClusterIconFn(incidentSeverity)}
                                                 markerIconFn={markerIconFn}
                                                 renderFeatureTooltip={renderFeatureTooltip}
                                                 getSelectedFeatures={getSelectedFeatures}/>
                    </LayersControl.Overlay>
                ))
            }
        </LayersControl>
        <LegendControl incidentLegend={incidentSeverityLegend}/>
    </GeoJSONMap>
}

export default IncidentMap;